/**
 * Client-side tool execution functions
 * These functions run in the browser and can interact with the DOM, browser APIs, and parent window
 */

/**
 * Add a course to the cart via parent window iframe communication
 */
async function addCourseToCart(args: {
  productId: number;
  courseId: number;
  categoryId: number;
  seatId: number;
  seatNumber: string;
  seatRoomName: string;
  portionOfClassRecordId?: number;
  casePresentationOption?: boolean;
}): Promise<string> {
  try {
    console.log("Adding course to cart with args:", args);
    // Post message to parent window
    // Map the data to match WordPress PHP expectations
    window.parent.postMessage(
      {
        type: "addToCart",
        data: {
          courseId: args.courseId,
          seatId: args.seatId,
          categoryId: args.categoryId,
          seatNumber: args.seatNumber,
          seatRoomName: args.seatRoomName,
          courseProductId: args.productId,
          addCourseToCart: 1,
          refreshOnSave: false,
          portionOfClass: args.portionOfClassRecordId,
          casePresentationOption: args.casePresentationOption,
        },
      },
      "*"
    );

    // Wait for response from parent
    return new Promise<string>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(
          new Error(
            "Timeout: Parent window did not respond within 30 seconds. Please try again."
          )
        );
      }, 30000); // 30 second timeout

      const handleMessage = (event: MessageEvent) => {
        if (event.data.type === "addToCartResponse") {
          clearTimeout(timeout);
          window.removeEventListener("message", handleMessage);

          if (event.data.data.success) {
            resolve(
              event.data.data.message +
                `${event.data.data.redirect_url ? ` View your cart here: ${event.data.data.redirect_url}` : ""}` ||
                `Successfully added course to cart.${event.data.data.redirect_url ? ` View your cart here: ${event.data.data.redirect_url}` : ""}`
            );
          } else {
            reject(
              new Error(
                event.data.data.message || "Failed to add course to cart"
              )
            );
          }
        }
      };

      window.addEventListener("message", handleMessage);
    });
  } catch (error) {
    // Catch any unexpected errors and provide helpful message
    const errorMessage =
      error instanceof Error ? error.message : "An unexpected error occurred";
    throw new Error(`Failed to add course to cart: ${errorMessage}`);
  }
}

/**
 * Export all client-side tool executions
 * Key must match the tool name defined in tools.ts
 */
export const clientToolExecutions = {
  addCourseToCart,
} as const;

// Type for tool execution function arguments
export type ClientToolExecutionArgs = {
  [K in keyof typeof clientToolExecutions]: Parameters<
    (typeof clientToolExecutions)[K]
  >[0];
};

// Helper type to check if a tool name is a valid client-side tool
export type ClientToolName = keyof typeof clientToolExecutions;

// Type guard to check if a tool is a client-side tool
export function isClientSideTool(toolName: string): toolName is ClientToolName {
  return toolName in clientToolExecutions;
}
